import { Chip } from '@mui/material';
import {
  Datagrid,
  FunctionField,
  List,
  ReferenceField,
  SelectInput,
  TextField,
  TextInput,
  useRecordContext,
} from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';

/**
 * Categories list — a light Phase-2 "tail" surface (D-categories-light): list +
 * edit only. NO create, NO delete, NO hierarchy/reparenting management — the
 * category tree is reference data that changes rarely, and restructuring it is
 * its own deliberate surface for later. This surface edits a category's OWN
 * fields (name, active), never the tree.
 *
 * Categories are MARKET-AGNOSTIC (the `Category` type has no `market` field —
 * the tree is shared across markets, D3), so there is deliberately NO region
 * filter here, unlike the market-bearing lists. Filter by active; `q` on name.
 * The parent is shown read-only.
 */

/** Active/inactive chip — the category's own status. */
export const CategoryStatusChip = () => {
  const record = useRecordContext();
  const active = record?.status === 'active';
  return (
    <Chip
      size="small"
      label={active ? 'Active' : 'Inactive'}
      color={active ? 'success' : 'default'}
      variant={active ? 'filled' : 'outlined'}
    />
  );
};

const categoryFilters = [
  <TextInput key="q" source="q" label="Search" alwaysOn resettable />,
  <SelectInput
    key="status"
    source="status"
    label="Status"
    choices={[
      { id: 'active', name: 'Active' },
      { id: 'inactive', name: 'Inactive' },
    ]}
  />,
];

/** The list itself. Exported for testing without the access wrapper. */
export const CategoryListView = () => (
  <List
    resource="categories"
    title="Categories"
    filters={categoryFilters}
    sort={{ field: 'displayOrder', order: 'ASC' }}
    perPage={50}
    exporter={false}
  >
    {/* Clicking a row opens the category detail. No bulk actions, and no bulk
        delete — categories are reference data, not deleted here. */}
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <TextField source="name" label="Name" />
      {/* Parent category name (read-only); blank for a top-level category. */}
      <ReferenceField
        label="Parent"
        source="parentId"
        reference="categories"
        sortable={false}
        link={false}
        emptyText="—"
      >
        <TextField source="name" />
      </ReferenceField>
      <FunctionField
        label="Status"
        sortBy="status"
        render={() => <CategoryStatusChip />}
      />
    </Datagrid>
  </List>
);

/** Gated entry point registered as the `categories` Resource's list. */
export const CategoryList = () => (
  <RequireAccess capability="categories:read">
    <CategoryListView />
  </RequireAccess>
);
